import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Users, PawPrint, Search, BarChart3, ArrowLeft, Shield, Trash2, ChevronLeft, ChevronRight, Pencil, Clock, CheckCircle, XCircle, MapPin, Link2, Move, Facebook, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { formatPhone } from '@/utils/formatPhone';
import { thumbStyle } from '@/lib/utils';
import { PET_COLORS, parseColorField } from '@/utils/petColors';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import Pagination from '@/components/Pagination';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAGE_SIZE = 10;
const PETS_PAGE_SIZE = 25; // publicaciones del backoffice

// Badge de estado para el listado de publicaciones del admin. 'active' no muestra badge
// (es el caso normal); el resto sí, para distinguir resueltas/inactivas/eliminadas/pendientes.
const PET_STATUS_BADGE = {
  resolved: { label: 'Resuelta', className: 'bg-green-100 text-green-700 border-green-300' },
  inactive: { label: 'Inactiva', className: 'bg-gray-100 text-gray-600 border-gray-300' },
  deleted: { label: 'Eliminada', className: 'bg-red-100 text-red-700 border-red-300' },
  pending_review: { label: 'En revisión', className: 'bg-yellow-100 text-yellow-700 border-yellow-300' },
};

function PetStatusBadge({ status }) {
  const cfg = PET_STATUS_BADGE[status];
  if (!cfg) return null;
  return <Badge className={`rounded-full ${cfg.className}`}>{cfg.label}</Badge>;
}

// Custom marker icon in coral color
const coralIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
      <circle cx="16" cy="16" r="10" fill="#FF8C66" stroke="#FF7A50" stroke-width="2"/>
      <circle cx="16" cy="16" r="4" fill="white"/>
    </svg>
  `),
  iconSize: [32, 32],
  iconAnchor: [16, 16],
  popupAnchor: [0, -16],
});

function MapCenterUpdater({ center }) {
  const map = useMap();
  useEffect(() => {
    if (!center) return;
    const id = requestAnimationFrame(() => {
      try {
        map.invalidateSize();
        map.setView(center, 15, { animate: false });
      } catch (e) {
        // el mapa se desmontó (diálogo cerrado) — ignorar
      }
    });
    return () => cancelAnimationFrame(id);
  }, [center, map]);
  return null;
}

function AdminLocationMarker({ position, onPositionChange }) {
  useMapEvents({
    click(e) {
      const { lat, lng } = e.latlng;
      onPositionChange(lat, lng);
    },
  });

  return position ? (
    <Marker
      position={position}
      icon={coralIcon}
      draggable={true}
      eventHandlers={{
        dragend: (e) => {
          const { lat, lng } = e.target.getLatLng();
          onPositionChange(lat, lng);
        },
      }}
    />
  ) : null;
}

function ThumbnailPositionEditor({ photos, position, zoom, onZoomChange, onPositionChange, onPhotosChange }) {
  const pickerRef = useRef(null);
  const isDragging = useRef(false);

  const parsePosition = (pos) => {
    const parts = (pos || '50% 50%').split(' ');
    return { x: parseFloat(parts[0]) || 50, y: parseFloat(parts[1]) || 50 };
  };
  const { x: posX, y: posY } = parsePosition(position);

  const updateFromEvent = (e) => {
    if (!pickerRef.current) return;
    const rect = pickerRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const x = Math.max(0, Math.min(100, Math.round(((clientX - rect.left) / rect.width) * 100)));
    const y = Math.max(0, Math.min(100, Math.round(((clientY - rect.top) / rect.height) * 100)));
    onPositionChange(`${x}% ${y}%`);
  };

  const setCoverPhoto = (idx) => {
    const newPhotos = [...photos];
    const [selected] = newPhotos.splice(idx, 1);
    newPhotos.unshift(selected);
    onPhotosChange(newPhotos);
  };

  const currentPhoto = photos[0];

  return (
    <div className="space-y-3">
      {photos.length > 1 && (
        <div>
          <p className="text-xs text-muted-foreground mb-1.5">Foto de portada</p>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {photos.map((photo, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setCoverPhoto(i)}
                className={`relative w-16 h-16 rounded-xl overflow-hidden shrink-0 border-2 transition-colors ${i === 0 ? 'border-primary' : 'border-transparent opacity-60 hover:opacity-100'}`}
              >
                <img src={photo} alt="" className="w-full h-full object-cover" draggable={false} />
                {i === 0 && (
                  <span className="absolute bottom-0 left-0 right-0 text-center text-[9px] font-semibold text-white bg-primary/80 py-0.5">
                    Portada
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {currentPhoto && (
        <>
          <p className="text-xs text-muted-foreground">
            Hacé clic o arrastrá sobre la imagen para centrar al animal en la miniatura.
          </p>
          <div
            ref={pickerRef}
            className="relative w-full rounded-xl overflow-hidden bg-black select-none"
            style={{ cursor: 'crosshair', aspectRatio: '4/3', maxHeight: '220px' }}
            onMouseDown={(e) => { isDragging.current = true; updateFromEvent(e); }}
            onMouseMove={(e) => { if (isDragging.current) updateFromEvent(e); }}
            onMouseUp={() => { isDragging.current = false; }}
            onMouseLeave={() => { isDragging.current = false; }}
            onTouchStart={(e) => { isDragging.current = true; updateFromEvent(e); }}
            onTouchMove={(e) => { if (isDragging.current) updateFromEvent(e); }}
            onTouchEnd={() => { isDragging.current = false; }}
          >
            <img
              src={currentPhoto}
              alt="Foto"
              className="w-full h-full"
              style={{ objectFit: 'contain', pointerEvents: 'none', userSelect: 'none' }}
              draggable={false}
            />
            <div
              style={{
                position: 'absolute',
                left: `${posX}%`,
                top: `${posY}%`,
                transform: 'translate(-50%, -50%)',
                width: '18px',
                height: '18px',
                borderRadius: '50%',
                border: '2.5px solid white',
                backgroundColor: '#FF8C66',
                boxShadow: '0 0 0 1.5px rgba(0,0,0,0.4)',
                pointerEvents: 'none',
              }}
            />
          </div>
          <div className="flex items-center gap-3">
            <label className="text-xs text-muted-foreground whitespace-nowrap">Zoom</label>
            <input
              type="range"
              min="1"
              max="3"
              step="0.05"
              value={zoom || 1}
              onChange={(e) => onZoomChange(parseFloat(e.target.value))}
              className="flex-1 accent-primary"
            />
            <span className="text-xs font-mono text-muted-foreground w-10 text-right">
              {(zoom || 1).toFixed(2)}x
            </span>
          </div>
          <div className="flex flex-wrap gap-4 items-start">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Vista tarjeta</p>
              <div className="w-32 h-24 rounded-xl overflow-hidden border border-border">
                <img
                  src={currentPhoto}
                  alt="Preview"
                  className="w-full h-full object-cover"
                  style={thumbStyle(position, zoom)}
                />
              </div>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Vista miniatura</p>
              <div className="w-16 h-16 rounded-xl overflow-hidden border border-border">
                <img
                  src={currentPhoto}
                  alt="Preview miniatura"
                  className="w-full h-full object-cover"
                  style={thumbStyle(position, zoom)}
                />
              </div>
            </div>
            <div className="flex-1 pt-5">
              <p className="text-xs font-mono bg-muted rounded px-2 py-1 text-muted-foreground">{position}</p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function MatchCard({ match, currentStatus, onStatusChange, onContact }) {
  const { lost_pet, found_pet } = match;

  const PetThumb = ({ pet, label }) => {
    const photo = pet?.photos?.[0];
    const src = photo
      ? (photo.startsWith('data:') ? photo : `data:image/jpeg;base64,${photo}`)
      : null;
    return (
      <div className="flex flex-col items-center gap-1.5 min-w-0 flex-1">
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</span>
        {src ? (
          <img
            src={src}
            alt={pet?.name || 'Mascota'}
            className="w-20 h-20 object-cover rounded-2xl flex-shrink-0 border border-border"
          />
        ) : (
          <div className="w-20 h-20 bg-muted rounded-2xl flex items-center justify-center flex-shrink-0">
            <PawPrint className="h-7 w-7 text-muted-foreground" />
          </div>
        )}
        <div className="text-center min-w-0 w-full px-1">
          <p className="font-semibold text-sm truncate">
            {pet ? (pet.name || <span className="italic text-muted-foreground">Sin nombre</span>) : <span className="italic text-muted-foreground text-xs">(eliminada)</span>}
          </p>
          {pet && (
            <Badge
              className="rounded-full text-xs mt-0.5 border-0"
              style={{ backgroundColor: pet.pet_type === 'cat' ? '#A1C6EA' : '#FF8C66', color: '#fff' }}
            >
              {pet.pet_type === 'dog' ? '🐶 Perro' : pet.pet_type === 'cat' ? '🐱 Gato' : '—'}
            </Badge>
          )}
          {pet?.location && (
            <p className="text-xs text-muted-foreground mt-0.5 truncate">
              <MapPin className="h-3 w-3 inline mr-0.5" />{pet.location}
            </p>
          )}
        </div>
      </div>
    );
  };

  const scoreColor =
    match.similarity_score >= 90 ? 'bg-[#A29BFE]' : 'bg-violet-400';

  return (
    <Card className="rounded-3xl border-border overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
      <CardContent className="p-4 space-y-3">
        {/* Score + match type + date */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className={`${scoreColor} text-white font-bold text-lg rounded-full px-4 py-0.5`}>
              {match.similarity_score.toFixed(0)}%
            </span>
            <Badge variant="outline" className="rounded-full text-xs">
              {match.match_type}
            </Badge>
          </div>
          <span className="text-xs text-muted-foreground">
            {match.created_at ? new Date(match.created_at).toLocaleDateString('es') : '—'}
          </span>
        </div>

        {/* Two pet thumbnails */}
        <div className="flex gap-2 items-start justify-around">
          <PetThumb pet={lost_pet} label="Perdida" />
          <div className="flex items-center justify-center self-center">
            <Link2 className="h-5 w-5 text-[#A29BFE]" />
          </div>
          <PetThumb pet={found_pet} label="Encontrada" />
        </div>

        {/* AI reasoning */}
        {match.ai_reasoning && (
          <p className="text-xs text-muted-foreground bg-muted/60 rounded-xl p-2 italic line-clamp-2">
            "{match.ai_reasoning}"
          </p>
        )}

        {/* Score breakdown */}
        <div className="flex gap-3 text-xs text-muted-foreground">
          <span>IA: <strong>{match.ai_score?.toFixed(0) ?? '—'}%</strong></span>
          <span>CLIP: <strong>{match.clip_score?.toFixed(0) ?? '—'}%</strong></span>
          <span>Reglas: <strong>{match.rule_score?.toFixed(0) ?? '—'}%</strong></span>
        </div>

        {/* Action buttons */}
        <div className="flex gap-2 pt-1 border-t border-border/50 justify-end flex-wrap">
          {currentStatus === 'active' && (
            <>
              <Button
                size="sm" variant="outline"
                className="rounded-full text-gray-500 border-gray-300 hover:bg-gray-100"
                onClick={() => onStatusChange(match.match_id, 'hidden')}
              >
                Ocultar
              </Button>
              <Button
                size="sm" variant="outline"
                className="rounded-full text-blue-500 border-blue-300 hover:bg-blue-50"
                onClick={() => onContact(match)}
                disabled={!!match.contacted_at}
              >
                {match.contacted_at ? 'Contactado' : 'Contactar'}
              </Button>
              <Button
                size="sm"
                className="rounded-full bg-green-600 hover:bg-green-700 text-white"
                onClick={() => onStatusChange(match.match_id, 'finalized')}
              >
                Finalizar
              </Button>
            </>
          )}
          {(currentStatus === 'hidden' || currentStatus === 'finalized') && (
            <Button
              size="sm" variant="outline"
              className="rounded-full border-[#FF8C66] text-[#FF8C66] hover:bg-orange-50"
              onClick={() => onStatusChange(match.match_id, 'active')}
            >
              Restaurar
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

const AdminPanel = ({ user }) => {
  const navigate = useNavigate();

  // Stats
  const [stats, setStats] = useState(null);

  // Users tab
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersPage, setUsersPage] = useState(0);
  const [usersSearch, setUsersSearch] = useState('');

  // Pets tab
  const [pets, setPets] = useState([]);
  const [petsTotal, setPetsTotal] = useState(0);
  const [petsPage, setPetsPage] = useState(0);
  const [petsSearch, setPetsSearch] = useState('');
  const [petsFilter, setPetsFilter] = useState('');
  const [animalFilter, setAnimalFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [approvedByFilter, setApprovedByFilter] = useState('');
  const [petsStatusFilter, setPetsStatusFilter] = useState('');
  const [admins, setAdmins] = useState([]);

  // Pending tab
  const [pendingPets, setPendingPets] = useState([]);
  const [pendingTotal, setPendingTotal] = useState(0);

  // Matches tab
  const [matches, setMatches] = useState([]);
  const [matchesTotal, setMatchesTotal] = useState(0);
  const [matchesPage, setMatchesPage] = useState(0);
  const [matchStatus, setMatchStatus] = useState('active');
  const [contactMatch, setContactMatch] = useState(null);
  const [contactLoading, setContactLoading] = useState(false);

  // Edit dialog
  const [editPet, setEditPet] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editFromPending, setEditFromPending] = useState(false);

  // Edit user dialog
  const [editUser, setEditUser] = useState(null);
  const [editUserForm, setEditUserForm] = useState({});

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/stats`, { withCredentials: true });
      setStats(res.data);
    } catch {
      toast.error('Error al cargar estadísticas');
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/users`, {
        params: { skip: usersPage * PAGE_SIZE, limit: PAGE_SIZE, search: usersSearch },
        withCredentials: true,
      });
      setUsers(res.data.users);
      setUsersTotal(res.data.total);
    } catch {
      toast.error('Error al cargar usuarios');
    }
  }, [usersPage, usersSearch]);

  const fetchPets = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/pets`, {
        params: { skip: petsPage * PETS_PAGE_SIZE, limit: PETS_PAGE_SIZE, search: petsSearch, pet_type: petsFilter, animal_type: animalFilter, date_from: dateFrom, date_to: dateTo, approved_by: approvedByFilter, status: petsStatusFilter },
        withCredentials: true,
      });
      setPets(res.data.pets);
      setPetsTotal(res.data.total);
    } catch {
      toast.error('Error al cargar publicaciones');
    }
  }, [petsPage, petsSearch, petsFilter, animalFilter, dateFrom, dateTo, approvedByFilter, petsStatusFilter]);

  const fetchAdmins = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/admins`, { withCredentials: true });
      setAdmins(res.data.admins || []);
    } catch {
      // silencioso: el filtro queda vacío si falla
    }
  }, []);

  const fetchPending = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/pets/pending`, { withCredentials: true });
      setPendingPets(res.data.pets);
      setPendingTotal(res.data.total);
    } catch {
      toast.error('Error al cargar publicaciones pendientes');
    }
  }, []);

  const fetchMatches = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/matches`, {
        params: { status: matchStatus, min_score: 70, skip: matchesPage * PAGE_SIZE, limit: PAGE_SIZE },
        withCredentials: true,
      });
      setMatches(res.data.matches);
      setMatchesTotal(res.data.total);
    } catch {
      toast.error('Error al cargar coincidencias');
    }
  }, [matchStatus, matchesPage]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchAdmins(); }, [fetchAdmins]);
  useEffect(() => { fetchUsers(); }, [fetchUsers]);
  useEffect(() => { fetchPets(); }, [fetchPets]);
  useEffect(() => { fetchPending(); }, [fetchPending]);
  useEffect(() => { fetchMatches(); }, [fetchMatches]);

  const deleteUser = async (userId) => {
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { withCredentials: true });
      toast.success('Usuario eliminado');
      fetchUsers();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar usuario');
    }
  };

  const openEditUser = (u) => {
    setEditUser(u);
    setEditUserForm({
      name: u.name || '',
      role: u.role || 'user',
      phone: u.phone || '',
    });
  };

  const saveEditUser = async () => {
    try {
      await axios.put(`${API}/admin/users/${editUser.user_id}`, editUserForm, { withCredentials: true });
      toast.success('Usuario actualizado');
      setEditUser(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al actualizar usuario');
    }
  };

  const deletePet = async (petId) => {
    try {
      await axios.delete(`${API}/admin/pets/${petId}`, { withCredentials: true });
      toast.success('Publicación eliminada');
      fetchPets();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar publicación');
    }
  };

  const openEdit = (pet, fromPending = false) => {
    setEditPet(pet);
    setEditFromPending(fromPending);
    setEditForm({
      name: pet.name || '',
      breed: pet.breed || '',
      color: parseColorField(pet.color),
      size: pet.size || '',
      location: pet.location || '',
      latitude: pet.latitude || null,
      longitude: pet.longitude || null,
      date_lost_found: pet.date_lost_found || '',
      description: pet.description || '',
      contact: pet.contact || '',
      type: pet.type || 'lost',
      pet_type: pet.pet_type || '',
      status: pet.status || 'active',
      thumbnail_position: pet.thumbnail_position || '50% 50%',
      thumbnail_zoom: pet.thumbnail_zoom || 1,
      photos: pet.photos ? [...pet.photos] : [],
      url_facebook: pet.url_facebook || '',
    });
  };

  const saveEdit = async () => {
    try {
      await axios.put(`${API}/admin/pets/${editPet.pet_id}`, editForm, { withCredentials: true });
      toast.success('Publicación actualizada');
      setEditPet(null);
      if (editFromPending) fetchPending(); else fetchPets();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al actualizar publicación');
    }
  };

  const approvePet = async (petId) => {
    try {
      await axios.put(`${API}/admin/pets/${petId}/approve`, {}, { withCredentials: true });
      toast.success('Publicación aprobada');
      fetchPending();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al aprobar publicación');
    }
  };

  const rejectPet = async (petId) => {
    try {
      await axios.put(`${API}/admin/pets/${petId}/reject`, {}, { withCredentials: true });
      toast.success('Publicación rechazada');
      fetchPending();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al rechazar publicación');
    }
  };

  const handleMatchStatus = async (matchId, newStatus) => {
    if (newStatus === 'finalized') {
      const confirmed = window.confirm(
        'Al finalizar esta coincidencia, ambas mascotas serán marcadas como resueltas y desaparecerán de los listados públicos. Se eliminarán todas las otras coincidencias de ambas mascotas. ¿Continuar?'
      );
      if (!confirmed) return;
    }
    try {
      await axios.put(`${API}/admin/matches/${matchId}/status`, { status: newStatus }, { withCredentials: true });
      const label = {
        hidden: 'Coincidencia ocultada',
        active: 'Coincidencia restaurada',
        finalized: 'Coincidencia finalizada. Ambas mascotas marcadas como resueltas.'
      }[newStatus] || 'Estado actualizado';
      toast.success(label);
      fetchMatches();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al actualizar estado');
    }
  };

  const handleContact = (match) => {
    setContactMatch(match);
  };

  const confirmContact = async () => {
    if (!contactMatch) return;
    setContactLoading(true);
    try {
      await axios.post(`${API}/admin/matches/${contactMatch.match_id}/contact`, {}, { withCredentials: true });
      toast.success('Notificaciones enviadas a ambos usuarios');
      setContactMatch(null);
      fetchMatches();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409) {
        toast.info(detail || 'Ya se enviaron las notificaciones');
      } else {
        toast.error(detail || 'Error al enviar notificaciones');
      }
    } finally {
      setContactLoading(false);
    }
  };

  const changeMatchStatus = (newStatus) => {
    setMatchStatus(newStatus);
    setMatchesPage(0);
  };

  const usersMaxPage = Math.max(0, Math.ceil(usersTotal / PAGE_SIZE) - 1);
  const petsMaxPage = Math.max(0, Math.ceil(petsTotal / PETS_PAGE_SIZE) - 1);

  return (
    <div className="min-h-screen gradient-mesh">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-border sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <Button variant="ghost" size="sm" className="rounded-full shrink-0 px-2 sm:px-3" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="h-4 w-4 sm:mr-1" /> <span className="hidden sm:inline">Volver</span>
            </Button>
            <h1 className="text-base sm:text-xl font-fraunces font-semibold text-foreground truncate">Panel de Administración</h1>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Badge variant="outline" className="rounded-full gap-1">
              <Shield className="h-3 w-3" /> <span className="hidden sm:inline">Admin</span>
            </Badge>
            <span className="text-sm text-muted-foreground hidden md:inline">{user?.name}</span>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <Tabs defaultValue="panel" className="space-y-6">
          <TabsList className="rounded-2xl sm:rounded-full flex flex-wrap h-auto gap-1">
            <TabsTrigger value="panel" className="rounded-full">Panel</TabsTrigger>
            <TabsTrigger value="users" className="rounded-full">Usuarios</TabsTrigger>
            <TabsTrigger value="pets" className="rounded-full">Publicaciones</TabsTrigger>
            <TabsTrigger value="pending" className="rounded-full gap-1.5">
              <Clock className="h-3.5 w-3.5" />
              Pendientes
              {pendingTotal > 0 && (
                <Badge className="rounded-full h-5 min-w-5 px-1.5 text-xs bg-orange-500 text-white border-0">
                  {pendingTotal}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="matches" className="rounded-full gap-1.5">
              <Link2 className="h-3.5 w-3.5" />
              Coincidencias
            </TabsTrigger>
          </TabsList>

          {/* ========== STATS TAB ========== */}
          <TabsContent value="panel">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card className="rounded-3xl border-border">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Total Usuarios</CardTitle>
                  <Users className="h-5 w-5 text-primary" />
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-fraunces font-bold">{stats?.total_users ?? '—'}</p>
                </CardContent>
              </Card>
              <Card className="rounded-3xl border-border">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Perdidas Activas</CardTitle>
                  <PawPrint className="h-5 w-5 text-destructive" />
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-fraunces font-bold">{stats?.lost_active ?? '—'}</p>
                </CardContent>
              </Card>
              <Card className="rounded-3xl border-border">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Encontradas Activas</CardTitle>
                  <Search className="h-5 w-5 text-green-500" />
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-fraunces font-bold">{stats?.found_active ?? '—'}</p>
                </CardContent>
              </Card>
              <Card className="rounded-3xl border-border">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Total Coincidencias</CardTitle>
                  <BarChart3 className="h-5 w-5 text-accent" />
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-fraunces font-bold">{stats?.total_matches ?? '—'}</p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ========== USERS TAB ========== */}
          <TabsContent value="users" className="space-y-4">
            <Input
              placeholder="Buscar por nombre o email..."
              value={usersSearch}
              onChange={(e) => { setUsersSearch(e.target.value); setUsersPage(0); }}
              className="max-w-sm rounded-full"
            />
            {/* Desktop: tabla */}
            <Card className="rounded-3xl border-border overflow-hidden hidden md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Rol</TableHead>
                    <TableHead>Fecha</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.length === 0 ? (
                    <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">Sin resultados</TableCell></TableRow>
                  ) : users.map((u) => (
                    <TableRow key={u.user_id}>
                      <TableCell className="font-medium">{u.name}</TableCell>
                      <TableCell>{u.email}</TableCell>
                      <TableCell>
                        <Badge variant={u.role === 'admin' ? 'default' : 'secondary'} className="rounded-full">
                          {u.role === 'admin' ? 'Admin' : 'Usuario'}
                        </Badge>
                      </TableCell>
                      <TableCell>{u.created_at ? new Date(u.created_at).toLocaleDateString('es') : '—'}</TableCell>
                      <TableCell className="text-right flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEditUser(u)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        {u.user_id !== user?.user_id && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Eliminar usuario</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Se eliminará a <strong>{u.name}</strong> ({u.email}) junto con todas sus publicaciones y sesiones. Esta acción no se puede deshacer.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                <AlertDialogAction onClick={() => deleteUser(u.user_id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                                  Eliminar
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>

            {/* Móvil: tarjetas */}
            <div className="md:hidden space-y-3">
              {users.length === 0 ? (
                <Card className="rounded-3xl border-border">
                  <CardContent className="text-center text-muted-foreground py-8">Sin resultados</CardContent>
                </Card>
              ) : users.map((u) => (
                <Card key={u.user_id} className="rounded-3xl border-border">
                  <CardContent className="p-4 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-medium truncate">{u.name}</p>
                        <p className="text-sm text-muted-foreground break-all">{u.email}</p>
                      </div>
                      <Badge variant={u.role === 'admin' ? 'default' : 'secondary'} className="rounded-full shrink-0">
                        {u.role === 'admin' ? 'Admin' : 'Usuario'}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString('es') : '—'}
                    </p>
                    <div className="flex gap-2 pt-1 justify-end border-t border-border/50">
                      <Button variant="ghost" size="sm" className="gap-1" onClick={() => openEditUser(u)}>
                        <Pencil className="h-4 w-4" /> Editar
                      </Button>
                      {u.user_id !== user?.user_id && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive gap-1">
                              <Trash2 className="h-4 w-4" /> Eliminar
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Eliminar usuario</AlertDialogTitle>
                              <AlertDialogDescription>
                                Se eliminará a <strong>{u.name}</strong> ({u.email}) junto con todas sus publicaciones y sesiones. Esta acción no se puede deshacer.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancelar</AlertDialogCancel>
                              <AlertDialogAction onClick={() => deleteUser(u.user_id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                                Eliminar
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-2">
              <p className="text-sm text-muted-foreground">{usersTotal} usuario(s)</p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="rounded-full" disabled={usersPage === 0} onClick={() => setUsersPage(p => p - 1)}>
                  <ChevronLeft className="h-4 w-4" /> Anterior
                </Button>
                <Button variant="outline" size="sm" className="rounded-full" disabled={usersPage >= usersMaxPage} onClick={() => setUsersPage(p => p + 1)}>
                  Siguiente <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* ========== PENDING TAB ========== */}
          <TabsContent value="pending" className="space-y-4">
            {pendingPets.length === 0 ? (
              <Card className="rounded-3xl border-border">
                <CardContent className="flex flex-col items-center justify-center py-16 text-muted-foreground gap-2">
                  <CheckCircle className="h-10 w-10 text-green-400" />
                  <p className="font-medium">No hay publicaciones pendientes</p>
                  <p className="text-sm">Todas las publicaciones del scraper han sido revisadas</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {pendingPets.map((p) => (
                  <Card key={p.pet_id} className="rounded-3xl border-border overflow-hidden">
                    <CardContent className="p-0">
                      <div className="flex gap-4 p-4">
                        {/* Foto */}
                        {p.photos?.[0] ? (
                          <div className="w-28 h-28 rounded-2xl overflow-hidden flex-shrink-0">
                            <img
                              src={p.photos[0]}
                              alt={p.name || 'Mascota'}
                              className="w-full h-full object-cover"
                              style={thumbStyle(p.thumbnail_position, p.thumbnail_zoom)}
                            />
                          </div>
                        ) : (
                          <div className="w-28 h-28 bg-muted rounded-2xl flex items-center justify-center flex-shrink-0">
                            <PawPrint className="h-8 w-8 text-muted-foreground" />
                          </div>
                        )}
                        {/* Datos */}
                        <div className="flex-1 min-w-0 space-y-1.5">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-fraunces font-semibold text-foreground">
                              {p.name || 'Sin nombre'}
                            </span>
                            <Badge variant={p.type === 'lost' ? 'destructive' : 'default'} className="rounded-full text-xs">
                              {p.type === 'lost' ? 'Perdida' : 'Encontrada'}
                            </Badge>
                            <Badge variant="outline" className="rounded-full text-xs">
                              {p.pet_type === 'dog' ? '🐶 Perro' : '🐱 Gato'}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground truncate">📍 {p.location}</p>
                          <p className="text-sm text-muted-foreground">📅 {p.date_lost_found || '—'}</p>
                          {p.contact && <p className="text-sm text-muted-foreground">📞 {p.contact}</p>}
                          {p.url_facebook && (
                            <a
                              href={p.url_facebook}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-sm text-[#1877F2] hover:underline"
                            >
                              <Facebook className="h-3.5 w-3.5" /> Ver post original
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          )}
                          <p className="text-sm line-clamp-2">{p.description}</p>
                        </div>
                      </div>
                      {/* Acciones */}
                      <div className="flex gap-2 px-4 pb-4 pt-3 justify-end border-t border-border/50">
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="sm" className="rounded-full text-destructive hover:text-destructive gap-1">
                              <XCircle className="h-4 w-4" /> Rechazar
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Rechazar publicación</AlertDialogTitle>
                              <AlertDialogDescription>
                                Se eliminará permanentemente la publicación de <strong>{p.name || 'Sin nombre'}</strong>. Esta acción no se puede deshacer.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancelar</AlertDialogCancel>
                              <AlertDialogAction onClick={() => rejectPet(p.pet_id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                                Rechazar
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                        <Button variant="outline" size="sm" className="rounded-full gap-1" onClick={() => openEdit(p, true)}>
                          <Pencil className="h-4 w-4" /> Editar
                        </Button>
                        <Button size="sm" className="rounded-full gap-1 bg-green-600 hover:bg-green-700" onClick={() => approvePet(p.pet_id)}>
                          <CheckCircle className="h-4 w-4" /> Aprobar
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* ========== PETS TAB ========== */}
          <TabsContent value="pets" className="space-y-4">
            <div className="flex flex-wrap gap-3">
              <Input
                placeholder="Buscar por nombre, ubicación o raza..."
                value={petsSearch}
                onChange={(e) => { setPetsSearch(e.target.value); setPetsPage(0); }}
                className="max-w-sm rounded-full"
              />
              <div className="flex gap-2">
                {['', 'lost', 'found'].map((f) => (
                  <Button
                    key={f}
                    variant={petsFilter === f ? 'default' : 'outline'}
                    size="sm"
                    className="rounded-full"
                    onClick={() => { setPetsFilter(f); setPetsPage(0); }}
                  >
                    {f === '' ? 'Todas' : f === 'lost' ? 'Perdidas' : 'Encontradas'}
                  </Button>
                ))}
              </div>
              <div className="h-6 w-px bg-border hidden sm:block" />
              <div className="flex gap-2">
                {['', 'dog', 'cat'].map((a) => (
                  <Button
                    key={a}
                    variant={animalFilter === a ? 'default' : 'outline'}
                    size="sm"
                    className="rounded-full"
                    onClick={() => { setAnimalFilter(a); setPetsPage(0); }}
                  >
                    {a === '' ? 'Todos' : a === 'dog' ? '🐶 Perro' : '🐱 Gato'}
                  </Button>
                ))}
              </div>
              <div className="h-6 w-px bg-border hidden sm:block" />
              <div className="flex items-center gap-2">
                <label className="text-sm text-muted-foreground whitespace-nowrap">Desde</label>
                <Input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPetsPage(0); }} className="w-[150px] rounded-full" />
                <label className="text-sm text-muted-foreground whitespace-nowrap">Hasta</label>
                <Input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPetsPage(0); }} className="w-[150px] rounded-full" />
              </div>
              <div className="h-6 w-px bg-border hidden sm:block" />
              <Select value={approvedByFilter || 'all'} onValueChange={(v) => { setApprovedByFilter(v === 'all' ? '' : v); setPetsPage(0); }}>
                <SelectTrigger className="w-[200px] rounded-full">
                  <SelectValue placeholder="Aprobado por" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Aprobado por: todos</SelectItem>
                  {admins.map((a) => (
                    <SelectItem key={a.user_id} value={a.user_id}>{a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={petsStatusFilter || 'all'} onValueChange={(v) => { setPetsStatusFilter(v === 'all' ? '' : v); setPetsPage(0); }}>
                <SelectTrigger className="w-[170px] rounded-full">
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Estado: todas</SelectItem>
                  <SelectItem value="active">Activas</SelectItem>
                  <SelectItem value="resolved">Resueltas</SelectItem>
                  <SelectItem value="inactive">Inactivas</SelectItem>
                  <SelectItem value="deleted">Eliminadas</SelectItem>
                </SelectContent>
              </Select>
              {(animalFilter || dateFrom || dateTo || petsFilter || petsSearch || approvedByFilter || petsStatusFilter) && (
                <Button variant="ghost" size="sm" className="rounded-full text-muted-foreground" onClick={() => { setPetsFilter(''); setAnimalFilter(''); setDateFrom(''); setDateTo(''); setPetsSearch(''); setApprovedByFilter(''); setPetsStatusFilter(''); setPetsPage(0); }}>
                  Limpiar filtros
                </Button>
              )}
            </div>
            {/* Desktop: tabla */}
            <Card className="rounded-3xl border-border overflow-hidden hidden md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16">Foto</TableHead>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Ubicación</TableHead>
                    <TableHead>Publicado por</TableHead>
                    <TableHead>Aprobado por</TableHead>
                    <TableHead>Fecha</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pets.length === 0 ? (
                    <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">Sin resultados</TableCell></TableRow>
                  ) : pets.map((p) => (
                    <TableRow key={p.pet_id}>
                      <TableCell>
                        {p.photos?.[0] ? (
                          <div className="w-12 h-12 rounded-xl overflow-hidden border border-border">
                            <img
                              src={p.photos[0].startsWith('data:') ? p.photos[0] : `data:image/jpeg;base64,${p.photos[0]}`}
                              alt={p.name || 'Mascota'}
                              className="w-full h-full object-cover"
                              style={thumbStyle(p.thumbnail_position, p.thumbnail_zoom)}
                              loading="lazy"
                            />
                          </div>
                        ) : (
                          <div className="w-12 h-12 bg-muted rounded-xl flex items-center justify-center">
                            <PawPrint className="h-5 w-5 text-muted-foreground" />
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-1.5">
                          {p.name || 'Sin nombre'}
                          {p.url_facebook && (
                            <a
                              href={p.url_facebook}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="Ver post original en Facebook"
                              onClick={(e) => e.stopPropagation()}
                              className="text-[#1877F2] hover:opacity-80"
                            >
                              <Facebook className="h-3.5 w-3.5" />
                            </a>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap items-center gap-1">
                          <Badge variant={p.type === 'lost' ? 'destructive' : 'default'} className="rounded-full">
                            {p.type === 'lost' ? 'Perdida' : 'Encontrada'}
                          </Badge>
                          <PetStatusBadge status={p.status} />
                        </div>
                      </TableCell>
                      <TableCell>{p.location}</TableCell>
                      <TableCell>{p.owner_name}</TableCell>
                      <TableCell>{p.approved_by_name || '—'}</TableCell>
                      <TableCell>{p.created_at ? new Date(p.created_at).toLocaleDateString('es') : '—'}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openEdit(p)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Eliminar publicación</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Se eliminará la publicación de <strong>{p.name || 'Sin nombre'}</strong> y sus coincidencias asociadas. Esta acción no se puede deshacer.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                <AlertDialogAction onClick={() => deletePet(p.pet_id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                                  Eliminar
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>

            {/* Móvil: tarjetas */}
            <div className="md:hidden space-y-3">
              {pets.length === 0 ? (
                <Card className="rounded-3xl border-border">
                  <CardContent className="text-center text-muted-foreground py-8">Sin resultados</CardContent>
                </Card>
              ) : pets.map((p) => (
                <Card key={p.pet_id} className="rounded-3xl border-border">
                  <CardContent className="p-4 space-y-2">
                    <div className="flex items-start gap-3">
                      {p.photos?.[0] ? (
                        <div className="w-14 h-14 rounded-xl overflow-hidden border border-border flex-shrink-0">
                          <img
                            src={p.photos[0].startsWith('data:') ? p.photos[0] : `data:image/jpeg;base64,${p.photos[0]}`}
                            alt={p.name || 'Mascota'}
                            className="w-full h-full object-cover"
                            style={thumbStyle(p.thumbnail_position, p.thumbnail_zoom)}
                            loading="lazy"
                          />
                        </div>
                      ) : (
                        <div className="w-14 h-14 bg-muted rounded-xl flex items-center justify-center flex-shrink-0">
                          <PawPrint className="h-6 w-6 text-muted-foreground" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0 space-y-2">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center gap-1.5 font-medium min-w-0">
                            <span className="truncate">{p.name || 'Sin nombre'}</span>
                            {p.url_facebook && (
                              <a
                                href={p.url_facebook}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Ver post original en Facebook"
                                className="text-[#1877F2] hover:opacity-80 shrink-0"
                              >
                                <Facebook className="h-3.5 w-3.5" />
                              </a>
                            )}
                          </div>
                          <div className="flex flex-wrap items-center gap-1 shrink-0 justify-end">
                            <Badge variant={p.type === 'lost' ? 'destructive' : 'default'} className="rounded-full">
                              {p.type === 'lost' ? 'Perdida' : 'Encontrada'}
                            </Badge>
                            <PetStatusBadge status={p.status} />
                          </div>
                        </div>
                        <p className="text-sm text-muted-foreground">📍 {p.location}</p>
                        <p className="text-sm text-muted-foreground">👤 {p.owner_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {p.created_at ? new Date(p.created_at).toLocaleDateString('es') : '—'}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2 pt-1 justify-end border-t border-border/50">
                      <Button variant="ghost" size="sm" className="gap-1" onClick={() => openEdit(p)}>
                        <Pencil className="h-4 w-4" /> Editar
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive gap-1">
                            <Trash2 className="h-4 w-4" /> Eliminar
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Eliminar publicación</AlertDialogTitle>
                            <AlertDialogDescription>
                              Se eliminará la publicación de <strong>{p.name || 'Sin nombre'}</strong> y sus coincidencias asociadas. Esta acción no se puede deshacer.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction onClick={() => deletePet(p.pet_id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                              Eliminar
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-2">
              <p className="text-sm text-muted-foreground">
                {petsTotal} publicación(es) — Página {petsPage + 1} de {petsMaxPage + 1}
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <Button variant="outline" size="sm" className="rounded-full" disabled={petsPage === 0} onClick={() => setPetsPage(p => Math.max(0, p - 1))}>
                  <ChevronLeft className="h-4 w-4" /> Anterior
                </Button>
                <Pagination page={petsPage} totalPages={petsMaxPage + 1} onPageChange={setPetsPage} />
                <Button variant="outline" size="sm" className="rounded-full" disabled={petsPage >= petsMaxPage} onClick={() => setPetsPage(p => p + 1)}>
                  Siguiente <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* ========== MATCHES TAB ========== */}
          <TabsContent value="matches" className="space-y-4">
            <Tabs value={matchStatus} onValueChange={changeMatchStatus} className="space-y-4">
              <TabsList className="rounded-full">
                <TabsTrigger value="active" className="rounded-full">Activos</TabsTrigger>
                <TabsTrigger value="hidden" className="rounded-full">Ocultos</TabsTrigger>
                <TabsTrigger value="finalized" className="rounded-full">Finalizados</TabsTrigger>
              </TabsList>
              <TabsContent value={matchStatus}>
                {matches.length === 0 ? (
                  <Card className="rounded-3xl border-border">
                    <CardContent className="flex flex-col items-center justify-center py-16 text-muted-foreground gap-2">
                      <Link2 className="h-10 w-10 text-[#A29BFE]" />
                      <p className="font-medium">No hay coincidencias en esta sección</p>
                      <p className="text-sm">
                        {matchStatus === 'active' && 'Las coincidencias con puntaje ≥ 70% aparecerán aquí.'}
                        {matchStatus === 'hidden' && 'Las coincidencias ocultadas aparecerán aquí.'}
                        {matchStatus === 'finalized' && 'Las coincidencias finalizadas aparecerán aquí.'}
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {matches.map((m) => (
                      <MatchCard
                        key={m.match_id}
                        match={m}
                        currentStatus={matchStatus}
                        onStatusChange={handleMatchStatus}
                        onContact={handleContact}
                      />
                    ))}
                  </div>
                )}
                <div className="flex items-center justify-between pt-2">
                  <p className="text-sm text-muted-foreground">{matchesTotal} coincidencia(s)</p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline" size="sm" className="rounded-full"
                      disabled={matchesPage === 0}
                      onClick={() => setMatchesPage(p => p - 1)}
                    >
                      <ChevronLeft className="h-4 w-4" /> Anterior
                    </Button>
                    <Button
                      variant="outline" size="sm" className="rounded-full"
                      disabled={matchesPage >= Math.max(0, Math.ceil(matchesTotal / PAGE_SIZE) - 1)}
                      onClick={() => setMatchesPage(p => p + 1)}
                    >
                      Siguiente <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </TabsContent>
            </Tabs>

            {/* Contact confirmation dialog */}
            <AlertDialog open={!!contactMatch} onOpenChange={(open) => !open && setContactMatch(null)}>
              <AlertDialogContent className="rounded-3xl">
                <AlertDialogHeader>
                  <AlertDialogTitle>Enviar notificación de contacto</AlertDialogTitle>
                  <AlertDialogDescription asChild>
                    <div>
                      <p>Se enviará un email a ambos dueños con los datos de contacto del otro y la información de la coincidencia ({contactMatch?.similarity_score?.toFixed(0)}%).</p>
                      <p className="mt-2"><strong>Perdida:</strong> {contactMatch?.lost_pet?.name || 'Sin nombre'} — {contactMatch?.lost_pet?.location}</p>
                      <p><strong>Encontrada:</strong> {contactMatch?.found_pet?.name || 'Sin nombre'} — {contactMatch?.found_pet?.location}</p>
                    </div>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="rounded-full" disabled={contactLoading}>
                    Cancelar
                  </AlertDialogCancel>
                  <AlertDialogAction
                    className="rounded-full bg-blue-500 hover:bg-blue-600"
                    onClick={confirmContact}
                    disabled={contactLoading}
                  >
                    {contactLoading ? 'Enviando...' : 'Enviar emails'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

          </TabsContent>
        </Tabs>
      </div>

      {/* Edit Pet Dialog */}
      <Dialog open={!!editPet} onOpenChange={(open) => !open && setEditPet(null)}>
        <DialogContent className="w-[calc(100vw-1.5rem)] sm:w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-fraunces">Editar Publicación</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-sm font-medium">Nombre</label>
                <Input value={editForm.name} onChange={(e) => setEditForm(f => ({ ...f, name: e.target.value }))} placeholder="Sin nombre" />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium">Raza</label>
                <Input value={editForm.breed} onChange={(e) => setEditForm(f => ({ ...f, breed: e.target.value }))} placeholder="Raza" />
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Color</label>
              <ToggleGroup
                type="multiple"
                value={editForm.color || []}
                onValueChange={(v) => setEditForm(f => ({ ...f, color: v }))}
                className="flex flex-wrap gap-1.5 justify-start"
              >
                {PET_COLORS.map((c) => (
                  <ToggleGroupItem
                    key={c.value}
                    value={c.value}
                    className="rounded-full px-2.5 py-0.5 text-xs border border-border data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:border-primary"
                  >
                    {c.label}
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-sm font-medium">Tamaño</label>
                <Select value={editForm.size} onValueChange={(v) => setEditForm(f => ({ ...f, size: v }))}>
                  <SelectTrigger><SelectValue placeholder="Tamaño" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="small">Pequeño</SelectItem>
                    <SelectItem value="medium">Mediano</SelectItem>
                    <SelectItem value="large">Grande</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-sm font-medium">Tipo de publicación</label>
                <Select value={editForm.type} onValueChange={(v) => setEditForm(f => ({ ...f, type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="lost">Perdida</SelectItem>
                    <SelectItem value="found">Encontrada</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium">Animal</label>
                <Select value={editForm.pet_type} onValueChange={(v) => setEditForm(f => ({ ...f, pet_type: v }))}>
                  <SelectTrigger><SelectValue placeholder="Animal" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="dog">Perro</SelectItem>
                    <SelectItem value="cat">Gato</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Ubicación</label>
              <Input value={editForm.location} onChange={(e) => setEditForm(f => ({ ...f, location: e.target.value }))} placeholder="Ubicación" />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium flex items-center gap-1">
                <MapPin className="h-3.5 w-3.5" /> Coordenadas
              </label>
              <div className="rounded-2xl overflow-hidden border-2 border-border" style={{ height: '250px', width: '100%' }}>
                <MapContainer
                  key={editPet?.pet_id || 'map'}
                  center={[
                    editForm.latitude || -31.4201,
                    editForm.longitude || -64.1888
                  ]}
                  zoom={editForm.latitude ? 15 : 13}
                  style={{ height: '100%', width: '100%' }}
                  zoomControl={true}
                  zoomAnimation={false}
                  fadeAnimation={false}
                  whenReady={(e) => {
                    requestAnimationFrame(() => {
                      try { e.target.invalidateSize(); } catch (_) {}
                    });
                  }}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <MapCenterUpdater center={[
                    editForm.latitude || -31.4201,
                    editForm.longitude || -64.1888
                  ]} />
                  <AdminLocationMarker
                    position={editForm.latitude ? [editForm.latitude, editForm.longitude] : null}
                    onPositionChange={(lat, lng) => setEditForm(f => ({ ...f, latitude: lat, longitude: lng }))}
                  />
                </MapContainer>
              </div>
              <p className="text-xs text-muted-foreground">
                {editForm.latitude
                  ? `${editForm.latitude.toFixed(6)}, ${editForm.longitude.toFixed(6)}`
                  : 'Hacé clic en el mapa para ubicar la mascota'}
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-sm font-medium">Fecha</label>
                <Input type="date" value={editForm.date_lost_found} onChange={(e) => setEditForm(f => ({ ...f, date_lost_found: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium">Estado</label>
                <Select value={editForm.status} onValueChange={(v) => setEditForm(f => ({ ...f, status: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Activa</SelectItem>
                    <SelectItem value="resolved">Resuelta</SelectItem>
                    <SelectItem value="inactive">Inactiva</SelectItem>
                    <SelectItem value="deleted">Eliminada</SelectItem>
                    <SelectItem value="pending_review">En revisión</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Contacto</label>
              <Input value={editForm.contact} onChange={(e) => setEditForm(f => ({ ...f, contact: formatPhone(e.target.value) }))} placeholder="Ej: 351 123 4567" />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium flex items-center gap-1.5">
                <Facebook className="h-3.5 w-3.5" /> URL de Facebook (origen)
              </label>
              <Input
                value={editForm.url_facebook}
                onChange={(e) => setEditForm(f => ({ ...f, url_facebook: e.target.value }))}
                placeholder="https://www.facebook.com/groups/.../posts/..."
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Descripción</label>
              <Textarea value={editForm.description} onChange={(e) => setEditForm(f => ({ ...f, description: e.target.value }))} rows={3} placeholder="Descripción" />
            </div>
            {editForm.photos?.length > 0 && (
              <div className="space-y-2 border-t border-border pt-4">
                <label className="text-sm font-medium flex items-center gap-1.5">
                  <Move className="h-3.5 w-3.5" /> Ajustar miniatura
                </label>
                <ThumbnailPositionEditor
                  photos={editForm.photos}
                  position={editForm.thumbnail_position || '50% 50%'}
                  zoom={editForm.thumbnail_zoom || 1}
                  onZoomChange={(z) => setEditForm(f => ({ ...f, thumbnail_zoom: z }))}
                  onPositionChange={(pos) => setEditForm(f => ({ ...f, thumbnail_position: pos }))}
                  onPhotosChange={(newPhotos) => setEditForm(f => ({ ...f, photos: newPhotos }))}
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setEditPet(null)}>Cancelar</Button>
            <Button className="rounded-full" onClick={saveEdit}>Guardar cambios</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={!!editUser} onOpenChange={(open) => !open && setEditUser(null)}>
        <DialogContent className="w-[calc(100vw-1.5rem)] sm:w-full max-w-md">
          <DialogHeader>
            <DialogTitle className="font-fraunces">Editar Usuario</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="space-y-1">
              <label className="text-sm font-medium">Nombre</label>
              <Input value={editUserForm.name} onChange={(e) => setEditUserForm(f => ({ ...f, name: e.target.value }))} placeholder="Nombre" />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Rol</label>
              <Select value={editUserForm.role} onValueChange={(v) => setEditUserForm(f => ({ ...f, role: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">Usuario</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Teléfono</label>
              <Input value={editUserForm.phone} onChange={(e) => setEditUserForm(f => ({ ...f, phone: formatPhone(e.target.value) }))} placeholder="Ej: 351 123 4567" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setEditUser(null)}>Cancelar</Button>
            <Button className="rounded-full" onClick={saveEditUser}>Guardar cambios</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPanel;
